/* eslint-disable react-hooks/exhaustive-deps */
import { useForm } from "react-hook-form";
import getCaptcha from "src/components/SendMessageForm/constants/captcha";
import { useCallback, useEffect, useMemo } from "react";
import { yupResolver } from '@hookform/resolvers/yup';
import { useRouter } from "next/router";
import * as yup from "yup";
import { ToastContainer, toast } from 'react-toastify';
import {
  nameLocales,
  inputLabelsLocales,
  emailLocales,
  requiredLocales,
  placeholderLocales,
  buttonLocales,
  titleLocales,
  toastSuccessMessage,
  captchaLocales
} from "./locales/data";
import 'react-toastify/dist/ReactToastify.css';


const stringSpaceRegExp = new RegExp('/\\p{L}/', 'u')
// const phoneRegExp =new RegExp('^[\\+]?[(]?[0-9]{3}[)]?[-\\s\\.]?[0-9]{3}[-\\s\\.]?[0-9]{4,6}$')


const SendMessageForm = () => {
  const { locale } = useRouter();

  const validationSchema = yup.object().shape({
    name: yup.string()
      .required(requiredLocales[locale])
      .test('isName', nameLocales[locale], (v) => (!v?.match(stringSpaceRegExp))),
    email: yup.string()
      .required(requiredLocales[locale])
      .email(emailLocales[locale]),
    phone: yup.string()
      .required(requiredLocales[locale]),
    message: yup.string()
      .required(requiredLocales[locale]),
  });
  const { register, handleSubmit, control, formState: { errors }, reset, setError, setValue } = useForm({
    resolver: yupResolver(validationSchema),
    mode: "onChange"
  });

  const {result, text} = useMemo(() => getCaptcha(), [errors]);

  const toastSuccess = useCallback(() => {
    toast.success(toastSuccessMessage[locale], {
      position: "top-right",
      autoClose: 5000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
    });
  }, [locale]);
  const toastError = useCallback((message) => {
    toast.error(message, {
      position: "top-right",
      autoClose: 5000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
    });
  }, []);

  const onSubmit = async (data) => {
    const captchaValue = data.captcha;
    if (Number(captchaValue) !== result) {
      setError('captcha', { type: 'custom', message: captchaLocales[locale] });
      return;
    }
    try {
      const response = await fetch("/api/contact", {
        "method": "POST",
        "headers": { "content-type": "application/json" },
        "body": JSON.stringify(data)
      })

      if (response.status === 200 && response.ok) {
        toastSuccess();
        reset();
      } else {
        toastError(response?.statusText)
      }
    } catch (error) {
      toastError(error?.message)
    }
  };

  useEffect(() => {
    if(Object.keys(errors).length) {
      setValue("captcha", "")
    }
  }, [errors,setValue]);

  return (
    <>
      <h2 className="text-xl md:text-2xl font-medium md:pb-4">{titleLocales[locale]}</h2>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="flex flex-col lg:flex-row justify-between">
          <Input
            label={inputLabelsLocales.name[locale]}
            name="name"
            register={register}
            required
            type="text"
            errors={errors}
            placeholder={placeholderLocales.name[locale]}
            width="w-full lg:w-[30%]"
          />
          <Input
            label={inputLabelsLocales.email[locale]}
            name="email"
            register={register}
            required
            type="email"
            errors={errors}
            placeholder={placeholderLocales.email[locale]}
            width="w-full lg:w-[30%]"
          />
          <Input
            label={inputLabelsLocales.phone[locale]}
            name="phone"
            register={register}
            required
            type="tel"
            errors={errors}
            placeholder={placeholderLocales.phone[locale]}
            width="w-full lg:w-[30%]"
            control={control}
          />
        </div>
        <div className="pt-0 lg:pt-12">
          <Input
            label={inputLabelsLocales.message[locale]}
            name="message"
            tag="textarea"
            type="text"
            register={register}
            errors={errors}
            required
            placeholder={placeholderLocales.message[locale]}
          />
        </div>
        <div className="pt-0 lg:pt-4 flex items-center gap-x-2 w-full lg:w-[30%]">
          <div className="h-full pt-6">
            <p className="text-3xl font-medium">{text}</p>
          </div>
          <Input
            name="captcha"
            type="number"
            register={register}
            errors={errors}
            required
            placeholder=""
            onChange={(e) => setValue('captcha', e.target.value.substring(0,3))}
          />
        </div>
        <p className="text-sm text-red-500 pt-1 first-letter:uppercase">{errors?.captcha?.message}</p>
        <button
          type="submit"
          className="mt-12 px-10 py-3 border-2 font-semibold tracking-wider text-sm rounded-md min-w-[12.5rem]
        inline-flex justify-center items-center uppercase bg-white border-secondary
        text-secondary hover:bg-secondary hover:text-white"
        >
          {buttonLocales[locale]}
        </button>
      </form>
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
    </>
  );
};

export default SendMessageForm;

const Input = ({
  label,
  register,
  required,
  name,
  placeholder,
  tag = "input",
  width = "",
  errors,
  type,
  onChange
}) => (
  <div className={`flex flex-col pt-6 ${width}`}>
    {label && (
      <label
        htmlFor={name}
        className="uppercase tracking-wider text-sm mb-1 text-sm"
      >
        {label}
      </label>
    )}
    {name === "phone" && (
      <input
        {...register(name, { required })}
        placeholder={placeholder}
        type={type}
        className="border-b py-2 border-black border-opacity-50 focus:outline-none text-sm lg:text-base rounded-none"
      />
    )}

    {tag === "input" && !["phone", "captcha"].includes(name) && (
      <input
        {...register(name, { required })}
        placeholder={placeholder}
        type={type}
        className="border-b py-2 border-black border-opacity-50 focus:outline-none text-sm lg:text-base after:block rounded-none"
      />
    )}
    {tag === "textarea" && (
      <textarea
        {...register(name, { required })}
        placeholder={placeholder}
        rows='5'
        type={type}
        className="border-b py-2 border-black border-opacity-50 focus:outline-none text-sm lg:text-base rounded-none resize-none"
      />
    )}
    {name === "captcha" && (
      <input
        {...register(name, { required })}
        placeholder={placeholder}
        type={type}
        className="max-w-[70px] text-3xl font-medium border-b border-black border-opacity-50 rounded-none"
        onChange={onChange}
      />
    )}
    {name !== "captcha" && (
      <p className="text-sm text-red-500 pt-1 first-letter:uppercase">{errors[name]?.message}</p>
    )}

  </div>
);